import { FC } from 'react';
import { Box, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import { GoldButton } from '@/components/GoldButton';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import SearchIcon from '@mui/icons-material/Search';

interface SignupCTAProps {
  variant: 'card' | 'inline';
}

interface BulletItem {
  icon: React.ReactNode;
  text: string;
}

const SignupCTA: FC<SignupCTAProps> = ({ variant }) => {
  const theme = useTheme();
  const bulletItems: BulletItem[] = [
    {
      icon: <TaskAltIcon sx={{ fontSize: 18, color: 'primary.main' }} />,
      text: 'Ensure they get it right the first time',
    },
    { icon: <EventBusyIcon sx={{ fontSize: 18, color: 'primary.main' }} />, text: 'Eliminate follow-up meetings' },
    { icon: <SearchIcon sx={{ fontSize: 18, color: 'primary.main' }} />, text: 'Find key moments instantly' },
  ];

  if (variant === 'card') {
    return (
      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
          border: `1px solid ${theme.palette.primary.main}30`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AutoAwesomeIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600}>
              Create Notes Like This
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Transform your feedback into AI-powered notes that:
          </Typography>

          <Stack spacing={1} sx={{ mb: 2.5 }}>
            {bulletItems.map((item) => (
              <Box key={item.text} sx={{ display: 'flex', alignItems: 'center' }}>
                {item.icon}
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Stack>

          <GoldButton fullWidth size="large" href="/login?action=signup">
            Start Your Free Trial
          </GoldButton>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
            No credit card required
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Inline variant for mobile
  const mobileItems: BulletItem[] = [
    { icon: <TaskAltIcon sx={{ fontSize: 18, color: 'white' }} />, text: 'Ensure they get it right the first time' },
    { icon: <EventBusyIcon sx={{ fontSize: 18, color: 'white' }} />, text: 'Eliminate follow-up meetings' },
    { icon: <SearchIcon sx={{ fontSize: 18, color: 'white' }} />, text: 'Find key moments instantly' },
  ];

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        textAlign: 'center',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
        <AutoAwesomeIcon sx={{ color: 'white', mr: 1, fontSize: 28 }} />
        <Typography variant="h6" fontWeight={600}>
          Create Notes Like This
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
        Transform your feedback into AI-powered notes that:
      </Typography>
      <Stack spacing={1} sx={{ mb: 2 }}>
        {mobileItems.map((item) => (
          <Box key={item.text} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {item.icon}
            <Typography variant="body2" sx={{ ml: 1 }}>
              {item.text}
            </Typography>
          </Box>
        ))}
      </Stack>
      <GoldButton size="large" href="/login?action=signup" fullWidth>
        Start Your Free Trial
      </GoldButton>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, opacity: 0.85 }}>
        No credit card required
      </Typography>
    </Box>
  );
};

export default SignupCTA;
